import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

/**
 * Toast Item - Jednotlivá notifikace
 */
const ToastItem = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
      }, toast.duration || 5000);

      return () => clearTimeout(timer);
    }
  }, [toast, onRemove]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const iconConfig = {
    success: { icon: CheckCircle, color: '#4CAF50', borderColor: '#4CAF50' },
    error: { icon: AlertCircle, color: '#F44336', borderColor: '#F44336' },
    warning: { icon: AlertTriangle, color: '#FF9800', borderColor: '#FF9800' },
    info: { icon: Info, color: '#2196F3', borderColor: '#2196F3' },
  };

  const config = iconConfig[toast.type] || iconConfig.info;
  const Icon = config.icon;

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '16px 20px',
        boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
        borderLeft: `4px solid ${config.borderColor}`,
        minWidth: '320px',
        maxWidth: '480px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        animation: isExiting ? 'slideOutRight 300ms ease forwards' : 'slideInRight 300ms ease',
        position: 'relative',
      }}
    >
      <Icon size={20} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <p
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#333333',
              margin: '0 0 4px',
            }}
          >
            {toast.title}
          </p>
        )}
        <p
          style={{
            fontSize: '13px',
            color: '#666666',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {toast.message}
        </p>
      </div>

      <button
        onClick={handleClose}
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999999',
          flexShrink: 0,
          transition: 'all 150ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F5F5F5';
          e.currentTarget.style.color = '#333333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#999999';
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

/**
 * Toast Container - Kontejner pro všechny notifikace
 */
export const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
};

/**
 * Toast Context Hook - Pro jednoduché použití toastů
 * 
 * Příklad použití:
 * const { showToast } = useToast();
 * showToast({ type: 'success', title: 'Hotovo!', message: 'Zákazník byl uložen.' });
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (toast) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const success = (message, title = 'Úspěch') => {
    return showToast({ type: 'success', title, message });
  };

  const error = (message, title = 'Chyba') => {
    return showToast({ type: 'error', title, message });
  };

  const warning = (message, title = 'Upozornění') => {
    return showToast({ type: 'warning', title, message });
  };

  const info = (message, title = 'Informace') => {
    return showToast({ type: 'info', title, message });
  };

  return {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
    ToastContainer: () => <ToastContainer toasts={toasts} onRemove={removeToast} />,
  };
};

// Add keyframe for slideOutRight
const style = document.createElement('style');
style.textContent = `
  @keyframes slideOutRight {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(20px);
    }
  }
`;
document.head.appendChild(style);

export default ToastItem;
